// Orkestra Meetings CP2 — public guest join page at /meet/:roomToken.
//
// Anonymous-reachable (no app session). Flow:
//   card    — enter a display name, press Join
//   joining — mint a LiveKit token via the meeting-guest-token edge fn
//             (verify_jwt=false; the unguessable slug is the capability).
//             Minting happens on Join, NOT on load, so the token's TTL
//             starts when the guest actually joins.
//   in_call — mount GuestVideoRoom with the minted token
//   left    — "you left" card with Rejoin (re-mints; the join window may
//             have moved, so errors re-map exactly as on first join)
//
// All four fn reject paths are mapped to friendly copy. The name + card
// stay put on error so the Join button doubles as "try again".

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { Video, Loader2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import GuestVideoRoom from '@/components/meet/GuestVideoRoom'

type Mode = 'card' | 'joining' | 'in_call' | 'left'

interface Minted {
  token: string
  serverUrl: string
  title: string
}

interface JoinError {
  message: string
  /** true → the situation may resolve (too-early / transient); the card
   *  keeps the Join button prominent as a retry. */
  retryable: boolean
}

// Map an edge-fn failure (HTTP status + body `code`) to guest-facing copy.
// Exported for unit assertion — it's a pure function of (status, code).
export function mapError(status: number, code: string): JoinError {
  if (status === 404) return { message: "This meeting link isn’t valid.", retryable: false }
  if (status === 410) return { message: 'This meeting was canceled.', retryable: false }
  if (status === 403 && code === 'too_early') {
    return {
      message: 'This meeting hasn’t started yet — you can join up to 15 minutes before the scheduled start.',
      retryable: true,
    }
  }
  if (status === 403 && code === 'ended') return { message: 'This meeting has ended.', retryable: false }
  return { message: 'Something went wrong joining the meeting. Please try again.', retryable: true }
}

export default function MeetJoin() {
  const { roomToken } = useParams<{ roomToken: string }>()
  const [name, setName] = useState('')
  const [mode, setMode] = useState<Mode>('card')
  const [error, setError] = useState<JoinError | null>(null)
  const [minted, setMinted] = useState<Minted | null>(null)

  const trimmed = name.trim()
  const nameValid = trimmed.length >= 1 && trimmed.length <= 60

  const join = async () => {
    if (!nameValid || !roomToken) return
    setMode('joining')
    setError(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke<{
        token: string; server_url: string; room: string
        meeting: { title: string; start_time: string; end_time: string }
      }>('meeting-guest-token', { body: { room_token: roomToken, display_name: trimmed } })

      if (fnErr) {
        // supabase-js surfaces non-2xx as FunctionsHttpError with the Response
        // on `.context`; read the status + our `code` field for precise mapping.
        let status = 0
        let code = ''
        const ctx = (fnErr as unknown as { context?: Response }).context
        if (ctx && typeof ctx.status === 'number') {
          status = ctx.status
          try { const body = await ctx.json(); code = (body?.code as string) ?? '' } catch { /* keep default */ }
        }
        setError(mapError(status, code))
        setMode(minted ? 'left' : 'card')
        return
      }

      if (!data?.token || !data?.server_url) {
        setError({ message: 'Something went wrong joining the meeting. Please try again.', retryable: true })
        setMode(minted ? 'left' : 'card')
        return
      }

      setMinted({ token: data.token, serverUrl: data.server_url, title: data.meeting?.title ?? 'Meeting' })
      setMode('in_call')
    } catch {
      setError({ message: 'We couldn’t reach the meeting service. Check your connection and try again.', retryable: true })
      setMode(minted ? 'left' : 'card')
    }
  }

  if (mode === 'in_call' && minted) {
    return (
      <GuestVideoRoom
        token={minted.token}
        serverUrl={minted.serverUrl}
        displayName={trimmed}
        meetingTitle={minted.title}
        onLeave={() => setMode('left')}
      />
    )
  }

  const joining = mode === 'joining'
  const left = mode === 'left'

  return (
    <div style={{
      minHeight: '100vh', background: 'var(--canvas)', color: 'var(--ink-primary)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px 16px',
    }}>
      <div style={{
        width: '100%', maxWidth: 400,
        background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)',
        borderRadius: 16, padding: 28, boxShadow: 'var(--shadow-popover-mid)',
      }}>
        {/* Brand mark */}
        <div style={{
          width: 44, height: 44, borderRadius: 12, background: 'var(--accent-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
        }}>
          <Video style={{ width: 22, height: 22, color: 'var(--accent-text)' }} />
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px' }}>
          {left ? 'You left the meeting' : 'Join meeting'}
        </h1>
        <p style={{ fontSize: 14, color: 'var(--ink-secondary)', lineHeight: 1.5, margin: '0 0 20px' }}>
          {left
            ? 'You can rejoin while the meeting is still open.'
            : 'Enter your name to join the video meeting.'}
        </p>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--ink-tertiary)', marginBottom: 6 }}>
          Your name
        </label>
        <input
          type="text"
          value={name}
          maxLength={60}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && nameValid && !joining) join() }}
          placeholder="e.g. Jordan Lee"
          disabled={joining}
          autoFocus
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '11px 12px', fontSize: 15,
            background: 'var(--surface-sunken)', color: 'var(--ink-primary)',
            border: '1px solid var(--border-default)', borderRadius: 10,
            outline: 'none', marginBottom: 16,
          }}
        />

        {error && (
          <div role="alert" style={{
            background: 'var(--danger-dim)', border: '1px solid var(--danger-border)',
            color: 'var(--danger)', borderRadius: 10,
            padding: '10px 12px', fontSize: 13, lineHeight: 1.5, marginBottom: 16,
          }}>
            {error.message}
          </div>
        )}

        <button
          type="button"
          onClick={join}
          disabled={!nameValid || joining}
          style={{
            width: '100%', padding: '12px 16px', fontSize: 15, fontWeight: 700,
            background: 'var(--accent)', color: 'var(--accent-ink)',
            border: 'none', borderRadius: 10,
            cursor: (!nameValid || joining) ? 'default' : 'pointer',
            opacity: (!nameValid || joining) ? 0.55 : 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}>
          {joining && <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} />}
          {joining ? 'Joining…' : left ? 'Rejoin' : 'Join'}
        </button>
      </div>
    </div>
  )
}
